import { useCallback, useState } from 'react';
import { PositionResult, UserPosition, UserPositionResult } from 'shared/types';
import { useQuery } from '@tanstack/react-query';
import {
  getCurrentPosition,
  hasGeolocationPermissions,
} from './useUserPosition.utils';
import {
  PERMISSION_DENIED_MESSAGE,
  USER_POSITION_POLLING_INTERVAL_MS,
} from './useUserPosition.constants';

type UseUserPositionProps = {
  pollingEnabled?: boolean;
};

const getUserPositionWithPermissionCheck =
  async (): Promise<UserPositionResult> => {
    const [permissionEnabled, [error, coords]] = await Promise.all([
      hasGeolocationPermissions(),
      getCurrentPosition(),
    ]);

    /**
     * Confirm if a user has geolocation permissions.
     * Workaround mentioned in "hasGeolocationPermissions" fn.
     */
    const permissionDeniedError = Boolean(
      error &&
        (error.message === PERMISSION_DENIED_MESSAGE ||
          ('code' in error &&
            error.code === GeolocationPositionError.PERMISSION_DENIED)),
    );

    const locationEnabled = permissionEnabled && !permissionDeniedError;

    const position: PositionResult | null = locationEnabled
      ? {
          lat: coords?.coords.latitude,
          lng: coords?.coords.longitude,
          az: coords?.coords.heading ?? undefined,
          accuracy: coords?.coords.accuracy,
        }
      : null;

    return { locationEnabled, error, position };
  };

const initialData: UserPositionResult = {
  locationEnabled: false,
  error: null,
  position: null,
};

export const useUserPosition = (
  props: UseUserPositionProps = {},
): UserPosition => {
  const { pollingEnabled = false } = props;
  const [askForPermission, setAskForPermission] = useState(false);

  const { data: { locationEnabled, error, position } = initialData, refetch } =
    useQuery({
      queryKey: ['user-position'],
      queryFn: () => getUserPositionWithPermissionCheck(),
      refetchInterval: pollingEnabled
        ? USER_POSITION_POLLING_INTERVAL_MS
        : undefined,
      staleTime: USER_POSITION_POLLING_INTERVAL_MS,
      refetchOnWindowFocus: 'always',
    });

  const getUserPosition = useCallback(async (): Promise<UserPositionResult> => {
    const result = await refetch();
    return result.data ?? initialData;
  }, [refetch]);

  return {
    lat: position?.lat,
    lng: position?.lng,
    az: position?.az,
    accuracy: position?.accuracy,
    error,
    askForPermission,
    locationEnabled,
    getUserPosition,
    setAskForPermission,
  };
};
